import React, { useMemo } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { serviceProtocolI18n } from './i18n';

export default function ProtocolViewModal({
  Modal,
  Badge,
  show,
  protocol,
  onClose,
}) {
  const { language } = useLanguage();
  const serviceProtocolLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: serviceProtocolI18n });

  useEffect(() => {
    serviceProtocolI18n.changeLanguage(serviceProtocolLanguage);
  }, [serviceProtocolLanguage]);

  const steps = useMemo(() => {
    if (!protocol || !protocol.steps) return [];

    if (Array.isArray(protocol.steps)) {
      return protocol.steps
        .map((s) => {
          if (!s) return '';
          if (typeof s === 'string') return s;
          return s.name ?? s.titulo ?? '';
        })
        .filter((txt) => txt.trim().length > 0);
    }

    if (typeof protocol.steps === 'string') {
      return protocol.steps
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
    }

    return [];
  }, [protocol]);

  const title = protocol?.name || t('service_protocol_view_title_fallback');
  const businessLine = protocol?.lineName || protocol?.businessLineName || '';
  if (!Modal) return null;
  return (
    <Modal
      title={title}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('service_protocol_view_close')}
      primaryBtnAction={onClose}
      secondaryBtnTxt={undefined}
      width={720}
    >
      <div className='p-3'>
        {businessLine && (
          <p className='mini color-primary-500 mb-3'>
            {t('service_protocol_view_line')} {businessLine}
          </p>
        )}

        <p className='mb-3 fw-medium'>{t('service_protocol_view_steps')}</p>

        {steps.length === 0 ? (
          <p className='caption font-light mb-0'>
            {t('service_protocol_view_no_steps')}
          </p>
        ) : (
          <div className='d-flex flex-column gap-2'>
            {steps.map((step, index) => (
              <div key={index} className='d-flex align-items-start gap-3'>
                {Badge ? (
                  <Badge
                    text={String(index + 1)}
                    color='primary'
                    size='sm'
                    className='d-flex align-items-center justify-content-center rounded-circle'
                    style={{ minWidth: 32, height: 32 }}
                  />
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center rounded-circle'
                    style={{
                      minWidth: 32,
                      height: 32,
                      background: 'var(--primary-500)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {index + 1}
                  </div>
                )}
                <span>{step}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
